import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ROUTES } from '@/lib/routes';

type UserRole = 'admin' | 'organizador' | 'equipe' | 'arbitro' | 'atleta';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRoles: UserRole[];
  hasRole: (role: UserRole) => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    let initialSessionHandled = false;

    // Set up auth listener FIRST, then check existing session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Auto-recover from corrupted JWT: se o refresh falha (bad_jwt /
        // missing sub claim), o Supabase emite SIGNED_OUT — limpamos estado
        // e qualquer chamada subsequente já volta como anônima em vez de
        // manter um user fantasma que trava o app em loading infinito.
        if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' && !session) {
          setSession(null);
          setUser(null);
          setUserRoles([]);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        // Perf: NÃO refetch de roles em TOKEN_REFRESHED (roles não mudam a
        // cada refresh de JWT — ~1h) nem em INITIAL_SESSION duplicado
        // (getSession abaixo já dispara fetchUserRoles). Só refetch em
        // SIGNED_IN / USER_UPDATED reais. Corta ~70% das chamadas semanais
        // ao endpoint user_roles.
        const shouldRefetchRoles =
          !!session?.user &&
          event !== 'TOKEN_REFRESHED' &&
          !(event === 'INITIAL_SESSION' && initialSessionHandled);

        if (shouldRefetchRoles) {
          // Use setTimeout to avoid potential deadlock with Supabase client
          setTimeout(() => {
            fetchUserRoles(session!.user.id);
          }, 0);
        } else if (!session) {
          setUserRoles([]);
        }
      }
    );

    (async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        // Só dropa a sessão se for claramente JWT inválido/corrompido.
        // Erros de rede/transitórios NÃO devem deslogar o usuário.
        // Sintomas reportados: admin era deslogado ao executar uma ação
        // (ex.: conceder cortesia) — porque getUser() abaixo retornava
        // erro transitório e o código forçava signOut. Agora preservamos
        // a sessão local e deixamos o auto-refresh do supabase recuperar.
        const isFatalAuthError = (e: any) => {
          const code = e?.code || e?.error_code || '';
          const msg = (e?.message || '').toLowerCase();
          return (
            code === 'bad_jwt' ||
            code === 'invalid_jwt' ||
            code === 'session_not_found' ||
            msg.includes('missing sub claim') ||
            msg.includes('invalid jwt')
          );
        };

        if (error && isFatalAuthError(error)) {
          console.warn('[useAuth] JWT inválido detectado, limpando:', error);
          await supabase.auth.signOut().catch(() => {});
          setSession(null);
          setUser(null);
          setUserRoles([]);
          setLoading(false);
          return;
        }

        // Sessão sem user.id é tecnicamente inválida — limpa local apenas
        if (session && !session.user?.id) {
          await supabase.auth.signOut().catch(() => {});
          setSession(null);
          setUser(null);
          setUserRoles([]);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // NÃO chamamos getUser() aqui pois 403 transitório derrubava
          // sessões válidas. O onAuthStateChange já cuida de SIGNED_OUT
          // emitido pelo client quando o refresh token é realmente inválido.
          await fetchUserRoles(session.user.id);
        }
        initialSessionHandled = true;

        setLoading(false);
      } catch (err) {
        console.error('[useAuth] Erro ao recuperar sessão:', err);
        setLoading(false);
      }
    })();

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRoles = async (userId: string) => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching roles:', error);
    }
    
    if (!error && data) {
      setUserRoles(data.map(r => r.role as UserRole));
    }
    setLoading(false);
  };

  const hasRole = (role: UserRole) => userRoles.includes(role);

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error('Erro ao sair');
    } else {
      setUserRoles([]);
      navigate(ROUTES.AUTH);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, userRoles, hasRole, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
